import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useAuth as useClerkAuth } from "@clerk/clerk-react";
import type { GetToken } from "@clerk/types";

interface OptimizedAuthState {
  isSignedIn: boolean;
  isLoaded: boolean;
  getToken: GetToken;
  userId?: string | null;
  signOut: () => void;
}

const OptimizedAuthContext = createContext<OptimizedAuthState | undefined>(
  undefined
);

export function OptimizedAuthProvider({ children }: { children: ReactNode }) {
  const auth = useClerkAuth();

  // Use refs to store stable references to functions that shouldn't cause re-renders
  const getTokenRef = useRef(auth.getToken);
  const signOutRef = useRef(auth.signOut);

  // Always update refs to point to the latest functions from Clerk
  // This ensures we get fresh tokens even when the auth state changes
  getTokenRef.current = auth.getToken;
  signOutRef.current = auth.signOut;

  // Create a stable value object that only updates when auth state actually changes
  const authValue = useMemo(
    () => ({
      isSignedIn: auth.isSignedIn ?? false,
      isLoaded: auth.isLoaded ?? false,
      getToken: (options?: Parameters<GetToken>[0]) => {
        // Always get the most current getToken function
        const currentGetToken = getTokenRef.current;
        return currentGetToken(options);
      },
      userId: auth.userId,
      signOut: () => signOutRef.current(),
    }),
    [
      auth.isSignedIn,
      auth.isLoaded,
      auth.userId,
      // Notably excluding: sessionId, organization, user object, etc.
      // These change frequently but usually don't affect app rendering
    ]
  );

  return (
    <OptimizedAuthContext.Provider value={authValue}>
      {children}
    </OptimizedAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptimizedAuthContext(): OptimizedAuthState {
  const context = useContext(OptimizedAuthContext);
  if (context === undefined) {
    throw new Error(
      "useOptimizedAuthContext must be used within an OptimizedAuthProvider"
    );
  }
  return context;
}

// Hook for specific auth properties to prevent re-renders
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthStatus() {
  const { isSignedIn, isLoaded } = useOptimizedAuthContext();
  return useMemo(() => ({ isSignedIn, isLoaded }), [isSignedIn, isLoaded]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthToken() {
  const { getToken } = useOptimizedAuthContext();
  return getToken;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthUser() {
  const { userId } = useOptimizedAuthContext();
  return userId;
}
